import * as React from 'react';
import {
  Filter,
  FilterChangeEvent,
  Operators,
} from '@progress/kendo-react-data-tools';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { ASSET } from '../../utils/constants';
import { GetString } from '../StringResourceManager';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import esMessages from '../../utils/gridMessageKey.json';
import {
  saveHealthFilters,
  saveProductsHealthFilters,
} from '../../store/UserSettings/UserSettingsActions';
import { NodeVersionPropertyValues } from '../../Interfaces/Versions/NodeVersionPropertyValues';
loadMessages(esMessages, 'fr-CA');
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { isMatch } from '../../utils/textMappings';
import DynamicComboBox from '../DynamicComboBox';

const useStyles = makeStyles(() =>
  createStyles({
    filterArea: {
      Height: '100%',
      width: '100%',
      overflow: 'auto',
    },
  }),
);

interface Props {
  activeThreshold: number;
}

const ProductsHealthFilter: React.FC<Props> = ({
  activeThreshold,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const filter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.productsHealthFilter,
  );

  const onFilterChange = (event: FilterChangeEvent) => {
    saveProductsHealthFilters(dispatch, event.filter);
  };

  const siteNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumbers,
  );

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.divisions,
  );

  const clusters: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.clusters,
  );

  const laneNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.laneNumbers,
  );

  const nodeTypes: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.nodeTypes,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const CustomSiteNumberFilter = (props: any) => {
    const onChangeSiteNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeSiteNumber}
        data={siteNumbers}
        value={props.filter.value}
        placeholder={GetString('STORE_PLACEHOLDER')}
      />
    );
  };

  const CustomClusterFilter = (props: any) => {
    const onChangeCluster = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeCluster}
        data={clusters}
        value={props.filter.value}
        placeholder={GetString('CLUSTER_PLACEHOLDER')}
      />
    );
  };

  const CustomLaneNumberFilter = (props: any) => {
    const onChangeLaneNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneNumber}
        data={laneNumbers}
        value={props.filter.value}
        placeholder={GetString('LANE_PLACEHOLDER')}
      />
    );
  };

  const CustomNodeTypeFilter = (props: any) => {
    const onChangeNodeType = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeNodeType}
        data={nodeTypes}
        value={props.filter.value}
        placeholder={GetString('NODE_TYPE_PLACEHOLDER')}
      />
    );
  };

  const CustomDivisionFilter = (props: any) => {
    const onChangeDivision = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDivision}
        data={divisions}
        value={props.filter.value}
        placeholder={GetString('DIVISION_PLACEHOLDER')}
      />
    );
  };

  const CustomLaneStatusFilter = (props: any) => {
    const onChangeLaneStatusFilter = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: {
            Status: getLaneStatusKey(event.target.value),
            ActiveThreshold: activeThreshold,
          },
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneStatusFilter}
        data={[GetString('HEALTHY'), GetString('ERROR')]}
        value={getComboBoxValue(props.filter?.value?.Status) ?? ''}
        placeholder={GetString('LANE_STATUS')}
      />
    );
  };

  const getLaneStatusKey = (value: any) => {
    if (isMatch('HEALTHY', value))
      return ApiEnums.ProductDeploymentStatus.Healthy;
    else if (isMatch('ERROR', value))
      return ApiEnums.ProductDeploymentStatus.Error;
    else return ApiEnums.ProductDeploymentStatus.Unknown;
  };

  const getComboBoxValue = (value: ApiEnums.ProductDeploymentStatus) => {
    if (value == ApiEnums.ProductDeploymentStatus.Healthy)
      return GetString('HEALTHY');
    else if (value == ApiEnums.ProductDeploymentStatus.Error)
      return GetString('ERROR');
  };

  useEffect(() => {
    var newFilter: CompositeFilterDescriptor = filter;

    newFilter.filters?.map((data: any) => {
      if (
        data?.field !== undefined &&
        data?.field == ASSET.LANE_STATUS &&
        data?.value?.ActiveThreshold !== undefined
      ) {
        data.value.ActiveThreshold = activeThreshold;
      }
    });

    saveProductsHealthFilters(dispatch, newFilter);
  }, [activeThreshold]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <Filter
          value={filter}
          className={classes.filterArea}
          onChange={onFilterChange}
          fields={[
            {
              name: ASSET.FIELD_DIVISION_NAME,
              label: GetString('FIELD_DIVISION_LABEL'),
              filter: CustomDivisionFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_STORE_NAME,
              label: GetString('FIELD_STORE_LABEL'),
              filter: CustomSiteNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_CLUSTER_NAME,
              label: GetString('FIELD_CLUSTER_LABEL'),
              filter: CustomClusterFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_LANE_NAME,
              label: GetString('FIELD_LANE_LABEL'),
              filter: CustomLaneNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_NODETYPE_NAME,
              label: GetString('FIELD_NODETYPE_LABEL'),
              filter: CustomNodeTypeFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.LANE_STATUS,
              label: GetString('LANE_STATUS'),
              filter: CustomLaneStatusFilter,
              operators: Operators.text,
            },
          ]}
        />
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ProductsHealthFilter;
